import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const isImageUrl = (u) => /^https?:\/\/\S+$/i.test(u) || u.startsWith("/api/files/");

/** Adds one photo at a time to a gallery — upload a file, or paste a URL and press Add. */
export default function GalleryAdder({ onAdd, testId = "gallery-adder" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image too large (max 8MB)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onAdd(`${BACKEND_URL}${r.data.url}`);
      toast.success("Photo added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const addUrl = () => {
    const v = url.trim();
    if (!isImageUrl(v)) return toast.error("Paste a full image link starting with https://");
    onAdd(v);
    setUrl("");
    toast.success("Photo added");
  };

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={testId}>
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-btn`}
        className="inline-flex items-center gap-2 border border-white/15 hover:border-[#F5B041]/60 h-10 px-4 small-caps text-[10px] transition disabled:opacity-50">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
        {busy ? "Uploading…" : "Upload photo"}
      </button>
      <div className="flex items-center gap-2 flex-1 min-w-[220px]">
        <input value={url} onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
          placeholder="…or paste an image link"
          data-testid={`${testId}-url`}
          className="flex-1 rounded-none bg-transparent border border-white/10 h-10 px-3 text-sm focus:border-[#C04000] outline-none" />
        <button type="button" onClick={addUrl} disabled={!url.trim()} data-testid={`${testId}-add`}
          className="inline-flex items-center gap-1 border border-white/15 hover:border-[#F5B041]/60 h-10 px-4 small-caps text-[10px] transition disabled:opacity-40">
          <Plus size={13} /> Add
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testId}-input`} />
    </div>
  );
}
